import React from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/atoms'
import { Modal } from '../organisms/modals/Modal'

interface ConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'info'
  loading?: boolean
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  loading = false
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          iconColor: 'text-red-500',
          confirmButtonClass: 'bg-red-600 hover:bg-red-700 text-white',
          borderColor: 'border-red-200'
        }
      case 'warning':
        return {
          iconColor: 'text-yellow-500',
          confirmButtonClass: 'bg-yellow-600 hover:bg-yellow-700 text-white',
          borderColor: 'border-yellow-200'
        }
      case 'info':
        return {
          iconColor: 'text-blue-500',
          confirmButtonClass: 'bg-blue-600 hover:bg-blue-700 text-white',
          borderColor: 'border-blue-200'
        }
      default:
        return {
          iconColor: 'text-red-500',
          confirmButtonClass: 'bg-red-600 hover:bg-red-700 text-white',
          borderColor: 'border-red-200'
        }
    }
  }

  const styles = getVariantStyles()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      contentClassName="max-w-md bg-background border border-border"
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center ${styles.borderColor} border-2`}>
            <AlertTriangle className={`w-5 h-5 ${styles.iconColor}`} />
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {title}
            </h3>
            <p className="text-gray-600 mb-6">
              {message}
            </p>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2"
              >
                {cancelText}
              </Button>
              <Button
                onClick={onConfirm}
                disabled={loading}
                className={`px-4 py-2 ${styles.confirmButtonClass}`}
                isLoading={loading}
              >
                {confirmText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
